import React, { useState } from 'react';
import './ConsultantBriefing.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const VERDICTS = [
  ['useful', 'Useful'],
  ['irrelevant', 'Irrelevant'],
  ['wrong_organisation', 'Wrong organisation'],
  ['stale', 'Stale'],
  ['incorrect_interpretation', 'Incorrect interpretation'],
];

function List({ items, empty }) {
  const values = (items || []).filter(Boolean);
  if (!values.length) return <p className="briefing-muted">{empty}</p>;
  return <ul>{values.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}</ul>;
}

export default function ConsultantBriefing({ intelligence, analysisId, allowFeedback = true, compact = false }) {
  const [feedback, setFeedback] = useState({ busy: '', message: '', error: '' });
  const briefing = intelligence?.briefing;
  const assessment = intelligence?.assessment;
  if (!briefing) return null;

  const sendFeedback = async (verdict) => {
    if (!analysisId || !assessment || feedback.busy) return;
    setFeedback({ busy: verdict, message: '', error: '' });
    try {
      const response = await fetch(`${API_URL}/api/analysis/${encodeURIComponent(analysisId)}/intelligence-feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assessment_id: assessment.assessment_id,
          assessment_version: assessment.processing_version,
          verdict,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail?.error || payload?.detail || 'Feedback could not be recorded');
      setFeedback({ busy: '', message: 'Feedback recorded; scores and weights were not changed.', error: '' });
    } catch (error) {
      setFeedback({ busy: '', message: '', error: String(error.message || error) });
    }
  };

  return (
    <section className={`consultant-briefing ${compact ? 'is-compact' : ''}`} data-testid="consultant-briefing" aria-labelledby={`briefing-title-${briefing.assessment_id}`}>
      <header className="consultant-briefing-header">
        <div>
          <p className="briefing-kicker">Consultant briefing</p>
          <h2 id={`briefing-title-${briefing.assessment_id}`}>{briefing.headline}</h2>
        </div>
        <div className="briefing-meta" aria-label="Briefing status">
          <span>{briefing.evidence_strength} evidence</span>
          <span>{briefing.timing}</span>
          {briefing.simulated && <span className="is-simulated">Simulated</span>}
        </div>
      </header>

      {briefing.degraded && (
        <div className="briefing-degraded" role="status">Insufficient evidence: {briefing.degraded_reason}</div>
      )}

      <div className="briefing-grid">
        <article><h3>Why this organisation?</h3><p>{briefing.why_this_organisation}</p></article>
        <article><h3>Why now?</h3><p>{briefing.why_now}</p></article>
        <article><h3>Fit to configured services</h3><p>{briefing.service_fit?.rationale}</p>
          {!!briefing.service_fit?.matched_services?.length && <p className="briefing-tags">{briefing.service_fit.matched_services.join(' · ')}</p>}
        </article>
        <article><h3>What might make us wrong?</h3><List items={briefing.uncertainties} empty="No contradiction was captured; continue to verify independently." /></article>
        <article><h3>What should I ask next?</h3><List items={briefing.next_questions} empty="No next question is supported by the current evidence." /></article>
        <article><h3>Suggested investigation</h3><List items={briefing.suggested_actions} empty="No action is suggested." /></article>
      </div>

      <details className="briefing-evidence">
        <summary>Inspect supporting evidence ({briefing.supporting_evidence?.length || 0})</summary>
        {(briefing.supporting_evidence || []).map((item) => (
          <article key={item.id}>
            <strong>{item.source?.identity || item.subject}</strong>
            <p>{item.source?.passage}</p>
            <small>
              {item.dates?.event_date ? `Event ${item.dates.event_date}` : 'Event date unknown'} · Published {item.dates?.publication_date || 'unknown'} · Retrieved {item.dates?.retrieval_date || 'unknown'}
            </small>
            {!!item.qualifications?.length && <List items={item.qualifications} />}
          </article>
        ))}
      </details>

      <footer className="briefing-footer">
        <span>{briefing.freshness_label}</span>
        <span>Assessment {briefing.processing_version}</span>
        {intelligence?.cost && <span>Provider/search cost ${intelligence.cost.recorded_cost_usd}</span>}
      </footer>

      {allowFeedback && analysisId && assessment && (
        <div className="briefing-feedback" aria-label="Briefing feedback">
          <span>Was this interpretation useful?</span>
          <div>{VERDICTS.map(([value, label]) => (
            <button key={value} type="button" disabled={!!feedback.busy} onClick={() => sendFeedback(value)} data-testid={`briefing-feedback-${value}`}>
              {feedback.busy === value ? 'Recording…' : label}
            </button>
          ))}</div>
          {feedback.message && <p role="status">{feedback.message}</p>}
          {feedback.error && <p role="alert">{feedback.error}</p>}
        </div>
      )}
    </section>
  );
}
